import { cn } from '@/lib/utils';

/**
 * RegattaReplay brand lockup v3 (overlay redesign, 2026-07): the little-boat
 * mark + wordmark taken from Nikhil's Figma logo (node 375:139). The mark is
 * the real 3-colour Figma vector — blue starboard sail (#176CDF), navy port
 * sail + hull, white play triangle — and the wordmark is mixed-case
 * "RegattaReplay™" (Regatta + Replay two-tone), not the retired uppercase set.
 *
 * The lockup renders on a TRANSPARENT background so it sits directly on the
 * video (the on-video brand bug) — no card. `variant` names the surface it
 * sits on: 'dark' (video / dark UI) lightens the navy parts to white and the
 * wordmark to near-white so the mark stays legible; 'light' keeps the Figma
 * literal for use on white.
 */

interface BrandLogoProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'light' | 'dark';
  className?: string;
}

/** Figma boat mark (node 375:110), viewBox 0 0 66.7477 68.3863. */
const BOAT_VIEWBOX = '0 0 66.7477 68.3863';

const iconPx = { sm: 22, md: 34, lg: 46 };
const wordPx = { sm: 11, md: 15, lg: 20 };

export function SailIcon({
  size = 28,
  variant = 'dark',
  mono,
  className,
}: {
  size?: number;
  /** Names the BACKGROUND the lockup sits on: only the wordmark adapts — the
   *  brand mark keeps the EXACT Figma colours on every surface. */
  variant?: 'light' | 'dark';
  /** When set, render the SOLID-WHITE caption boat (Figma 375:449) instead of
   *  the 3-colour brand boat: sail + hull go white and the play triangle is
   *  knocked out to this colour (the surface behind it). */
  mono?: string;
  className?: string;
}) {
  void variant;
  const isMono = typeof mono === 'string';
  // Brand boat (node 375:110): starboard sail #176CDF, port sail + hull #1A365D
  // navy, white play. Mono (node 375:449): white silhouette, play knocked out.
  const sail = isMono ? '#FFFFFF' : '#176CDF';
  const navy = isMono ? '#FFFFFF' : '#1A365D';
  const play = isMono ? (mono as string) : '#FFFFFF';
  return (
    <svg
      viewBox={BOAT_VIEWBOX}
      width={size}
      height={size}
      aria-hidden="true"
      className={cn('block shrink-0 select-none', className)}
    >
      <path fill={sail} d="M31.9277 0.640878C41.5004 -2.16323 52.9449 4.46885 60.5588 15.9528C66.3024 25.5226 68.1726 36.962 64.3878 46.4427C62.5177 52.1845 60.7362 55.7453 58.8661 57.704L33.8866 58.8164C33.8866 42.5703 31.9277 0.640878 31.9277 0.640878Z" />
      <path fill={play} d="M43.1931 25.7896V42.6592L54.4585 34.2022L43.1931 25.745V25.7896Z" />
      <path fill={navy} d="M66.7477 57.5708C66.7477 57.5708 66.1693 59.3504 34.6426 59.8401C3.11751 60.3298 0 59.8401 0 59.8401C0 59.8401 0.667212 68.3863 38.2054 68.3863C63.8455 68.3863 65.8176 65.9741 65.8176 64.4953C65.8176 62.6056 66.7034 57.6152 66.7034 57.6152L66.7477 57.5708Z" />
      <path fill={navy} d="M6.23337 57.303L30.5472 58.238V43.5051L6.23337 57.303Z" />
      <path fill={navy} d="M30.636 42.5703L31.2145 0.641039L26.8957 16.8876C23.5103 29.9293 17.3213 42.1252 8.86114 52.541L5.12079 57.303L30.636 42.5703Z" />
    </svg>
  );
}

export function BrandLogo({ size = 'md', variant = 'dark', className }: BrandLogoProps) {
  const dark = variant === 'dark';
  const fs = wordPx[size];
  return (
    <span
      className={cn('inline-flex items-center gap-2', className)}
      role="img"
      aria-label="RegattaReplay"
    >
      <SailIcon size={iconPx[size]} variant={variant} />
      <span
        className="flex items-baseline font-bold leading-none tracking-[-0.01em]"
        style={{ fontSize: fs }}
        aria-hidden="true"
      >
        {/* Figma wordmark: Regatta near-black (#0A0C0F) on light — lightened
            to near-white on dark so it reads on video; Replay is the exact
            Figma blue #176CDF on every surface. */}
        <span className={dark ? 'text-[#e9f2fb]' : 'text-[#0A0C0F]'}>Regatta</span>
        <span className="text-[#176CDF]">Replay</span>
        <span
          className={cn('font-semibold', dark ? 'text-[#e9f2fb]' : 'text-[#0A0C0F]')}
          style={{ fontSize: '0.5em', marginLeft: '0.12em', alignSelf: 'flex-start' }}
        >
          ™
        </span>
      </span>
      <span className="sr-only">Replay™</span>
    </span>
  );
}
